import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import {
    Radio,
    RadioGroup,
    FormControlLabel,
    Button,
    Box,
    CircularProgress,
    InputAdornment,
    Typography,
    Grid,
    TextField,
    Checkbox,
    ImageList,
    ImageListItem,
    Stack,
    Paper,
    Divider,
    useTheme,
    useMediaQuery,
    Collapse,
    Tooltip,
} from '@mui/material';
import AlertDialog from '../../AlertDialog/AlertDialog';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { setShowMessage } from '../../../store/shared.slice';
import { RootState } from '../../../reducers';
import { MembersVitalSigns } from '../../../Models/VitalSigns/MembersVitalSigns.model';
import Height from '../../../Models/VitalSigns/Height.model';
import {
    fetchMemberVitalSign,
    fetchVitalSignByMemberId,
    fetchVitalSignHistory,
    gotoPreviousMenu,
    saveMemberVitalsign,
    updateMemberVitalSign,
} from '../../../store/assessment.slice';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import bad from './../../../assets/images/1F61F.svg';
import verygood from './../../../assets/images/1F601.svg';
import ok from './../../../assets/images/1F610.svg';
import good from './../../../assets/images/1F642.svg';
import verybad from './../../../assets/images/1F912.svg';
import { preventTextInput, preventNumberInputMaxValue, copyObject } from '../../../utils/common';
import { useParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from '@mui/icons-material/Timeline';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Vital_Signs_Care_Circle_Demographics_PCP.pdf';

const isSeriesDataAvailable = (series: any[] | undefined) => {
    if (!series || !Array.isArray(series)) return false;
    for (const s of series) {
        if (s && Array.isArray(s.data) && s.data.length > 0) return true;
        if (s && Array.isArray(s) && s.length > 0) return true;
    }
    return false;
};

const ChartWrapper: React.FC<{ options: any; sx?: any }> = React.memo(({ options, sx = {} }) => {
    const hasData = isSeriesDataAvailable(options?.series);
    return (
        <Box sx={{ height: sx?.height ?? { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
            <div style={{ height: '100%', display: 'flex' }}>
                <div style={{ flex: 1 }}>
                    {hasData ? (
                        <HighchartsReact highcharts={Highcharts} containerProps={{ style: { height: '100%' } }} options={options} immutable={true} />
                    ) : (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No {options.title?.text} history available
                            </Typography>
                        </Box>
                    )}
                </div>
            </div>
        </Box>
    );
});

const emojisList = [
    {
        emoji: verybad,
        title: 'Very Bad',
    },
    {
        emoji: bad,
        title: 'Bad',
    },
    {
        emoji: ok,
        title: 'Ok',
    },
    {
        emoji: good,
        title: 'Good',
    },
    {
        emoji: verygood,
        title: 'Very Good',
    },
];

const VitalSigns: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = memberId || params.memberId;
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { vitalSign, historyData, fetchingVitalSign, isSaving } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [showPhysicalChart, setShowPhysicalChart] = useState(false);
    const [showVitalChart, setShowVitalChart] = useState(false);
    const [showHealthChart, setShowHealthChart] = useState(false);

    const [weightIsChanged, setWeightIsChanged] = useState(false);
    const [formIsValid, setFormIsValid] = useState(true);

    useEffect(() => {
        dispatch(fetchVitalSignHistory(memberId));
    }, [memberId, dispatch]);

    useEffect(() => {
        if (engagementDraft?.VitalsignAssessmentId) {
            dispatch(fetchMemberVitalSign(engagementDraft?.VitalsignAssessmentId));
        } else {
            dispatch(fetchVitalSignByMemberId(memberId));
        }
    }, [engagementDraft?.VitalsignAssessmentId, memberId, dispatch]);

    const handleSubmit = async (values: MembersVitalSigns) => {
        const requiredFields = [values.BpPulse, values.BpDiastolic, values.BpSystolic];

        if (requiredFields.some((field) => field === '' || field === null) && !values.RefusedForAssessment) {
            setFormIsValid(false);
            dispatch(setShowMessage(true, 'Please fill in required areas or select the Member refuses button', 'error'));
        } else {
            setFormIsValid(true);
            setShowSaveAlert(true);
        }
    };

    const handleOnConfirmToNext = async (vitalSignInfo: MembersVitalSigns, isWithoutComplete = false) => {
        let data = copyObject(vitalSignInfo);
        data.MemberId = memberId;
        if (vitalSignInfo.Height !== null && vitalSignInfo.Height !== undefined) {
            data.Height = new Height();
            data.Height.Feet = vitalSignInfo.Height?.Feet ?? Number(vitalSignInfo.Height.Feet);
            data.Height.Inch = vitalSignInfo.Height?.Inch ?? Number(vitalSignInfo.Height.Inch);
        }
        data.BpPulse = String(vitalSignInfo.BpPulse);
        data.BpSystolic = String(vitalSignInfo.BpSystolic);
        data.BpDiastolic = String(vitalSignInfo.BpDiastolic);
        if (Boolean(vitalSignInfo.BMI)) {
            data.BMI = vitalSignInfo.BMI;
        } else {
            delete data.BMI;
        }
        data.AssessmentStatus = isWithoutComplete ? StatusEnum.Completed : StatusEnum.InProgress;

        if (vitalSignInfo.Id) {
            data.Id = vitalSignInfo.Id;
            dispatch(updateMemberVitalSign(data, isWithoutComplete));
        } else {
            dispatch(saveMemberVitalsign(data, isWithoutComplete));
        }
    };

    // Calculate bmi
    const calculateBMI = async (feet: number, inch: number, weight: string, setFieldValue: Function) => {
        let bmi = '';
        if (feet && weight) {
            // 1 ft is 12 in
            let totHeightInInch = feet * 12 + inch;
            // Formula: 703 x weight(lbs) / [height(in)]2
            let heightSquare = totHeightInInch * totHeightInInch;
            bmi = ((703 * parseInt(weight)) / heightSquare).toFixed(2);
        } else {
            bmi = '';
        }
        setFieldValue('Height.Feet', feet);
        setFieldValue('Height.Inch', inch);
        setFieldValue('Weight', weight);
        setFieldValue('BMI', bmi);
    };

    const buildSeriesData = (data: any[], field: string) => {
        return data
            .map((item: any) => {
                const date = toDate(item.RecordingDate);
                const rawValue = item[field];

                if (rawValue === null || rawValue === undefined || rawValue === '') {
                    return null;
                }

                const value = Number(rawValue);
                // Ensure both date and value are valid numbers; allow 0 as a legitimate value
                if (!date || !Number.isFinite(date.getTime()) || !Number.isFinite(value)) {
                    return null;
                }

                return [date.getTime(), value];
            })
            .filter(Boolean)
            .sort((a: any, b: any) => a[0] - b[0]);
    };

    return (
        <Formik initialValues={vitalSign} onSubmit={(values: MembersVitalSigns) => handleSubmit(values)} enableReinitialize={true}>
            {({ values, setFieldValue, handleChange, handleBlur }: FormikProps<MembersVitalSigns>) => (
                <Form>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} mb={2}>
                        <Typography variant="h6">Vital Signs</Typography>
                        <Tooltip title="Vital Signs Help Document">
                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                        </Tooltip>
                    </Stack>
                    <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }} className="content-panel">
                        {fetchingVitalSign ? (
                            <Box display="flex" justifyContent="center" className="loader-container">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                                    <Grid container spacing={3}>
                                        <Grid size={12}>
                                            <Typography variant="h6" gutterBottom>
                                                Physical Measurements
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_Height} {values.IsHeightAi && isAIIcon(values.Height)}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1}>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                label="Feet"
                                                                name="Height.Feet"
                                                                value={values.Height?.Feet ?? ''}
                                                                onChange={(event) => {
                                                                    setIsChanged(true);
                                                                    setFieldValue('IsHeightAi', false);
                                                                    calculateBMI(
                                                                        parseFloat(event.currentTarget.value),
                                                                        values.Height?.Inch,
                                                                        values.Weight,
                                                                        setFieldValue
                                                                    );
                                                                }}
                                                                inputProps={{ min: 0, max: 9 }}
                                                                onKeyPress={(e) => {
                                                                    preventTextInput(e);
                                                                    preventNumberInputMaxValue(e, 9);
                                                                }}
                                                                size={isSmallScreen ? 'medium' : 'small'}
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                label="Inches"
                                                                name="Height.Inch"
                                                                value={values.Height?.Inch ?? ''}
                                                                onChange={(event) => {
                                                                    setIsChanged(true);
                                                                    setFieldValue('IsHeightAi', false);
                                                                    calculateBMI(
                                                                        values.Height?.Feet,
                                                                        parseFloat(event.currentTarget.value),
                                                                        values.Weight,
                                                                        setFieldValue
                                                                    );
                                                                }}
                                                                inputProps={{ min: 0, max: 11 }}
                                                                onKeyPress={(e) => {
                                                                    preventTextInput(e);
                                                                    preventNumberInputMaxValue(e, 11);
                                                                }}
                                                                size={isSmallScreen ? 'medium' : 'small'}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_Weight}
                                                            {values.IsWeightAi && isAIIcon(values.Weight)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Weight (lbs)"
                                                            name="Weight"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        borderColor: weightIsChanged ? 'inherit' : 'red',
                                                                    },
                                                                },
                                                            }}
                                                            value={values.Weight || ''}
                                                            onKeyPress={preventTextInput}
                                                            inputProps={{ min: 0 }}
                                                            onChange={(event) => {
                                                                setIsChanged(true);
                                                                setWeightIsChanged(true);
                                                                setFieldValue('IsWeightAi', false);
                                                                calculateBMI(
                                                                    values.Height?.Feet,
                                                                    values.Height?.Inch,
                                                                    event.currentTarget.value,
                                                                    setFieldValue
                                                                );
                                                            }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_BmiValuation} {values.IsBmiValuationAi && isAIIcon(values.BmiValuation)}
                                                        </Typography>
                                                        <RadioGroup
                                                            row
                                                            name="BmiValuation"
                                                            value={values.BmiValuation}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                setFieldValue('IsBmiValuationAi', false);
                                                                setIsChanged(true);
                                                            }}
                                                        >
                                                            <FormControlLabel value="Self-reported" control={<Radio size="small" />} label="Self-reported" />
                                                            <FormControlLabel value="Actual" control={<Radio size="small" />} label="Actual" />
                                                        </RadioGroup>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            BMI (Auto-calculated)
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            disabled
                                                            sx={{ 'MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.8)' } }}
                                                            value={values.BMI || ''}
                                                            name="BMI"
                                                            InputProps={{
                                                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                            }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid size={12}>
                                            <Button
                                                onClick={() => setShowPhysicalChart(!showPhysicalChart)}
                                                startIcon={<TimelineIcon />}
                                                endIcon={showPhysicalChart ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                sx={{ mb: 2 }}
                                                variant="outlined"
                                                size="small"
                                                disabled={historyData && historyData.length < 2}
                                            >
                                                {showPhysicalChart ? 'Hide' : 'Show'} Physical Measurements History
                                            </Button>
                                            <Collapse in={showPhysicalChart}>
                                                <Paper elevation={1} sx={{ p: 2 }}>
                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12, md: 12 }} pb={2}>
                                                            <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
                                                                <ChartWrapper
                                                                    options={{
                                                                        title: { text: 'Weight & BMI History' },

                                                                        chart: { type: 'line' },

                                                                        xAxis: {
                                                                            type: 'datetime',
                                                                            title: { text: 'Date' },

                                                                            tickInterval: 30 * 24 * 3600 * 1000, // fallback (approx month)

                                                                            tickPositioner: function (this: any) {
                                                                                const positions = [];

                                                                                const min = this.dataMin;
                                                                                const max = this.dataMax;

                                                                                if (
                                                                                    min == null ||
                                                                                    max == null ||
                                                                                    typeof min !== 'number' ||
                                                                                    typeof max !== 'number'
                                                                                ) {
                                                                                    return positions;
                                                                                }

                                                                                const start = new Date(min);
                                                                                start.setDate(1);
                                                                                start.setHours(0, 0, 0, 0);

                                                                                const end = new Date(max);

                                                                                let current = new Date(start);

                                                                                while (current <= end) {
                                                                                    positions.push(current.getTime());

                                                                                    // move to next month
                                                                                    current.setMonth(current.getMonth() + 1);
                                                                                }

                                                                                return positions;
                                                                            },

                                                                            labels: {
                                                                                step: 1,
                                                                                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                                                                                    const d = new Date(this.value as number);

                                                                                    return isNaN(d.getTime()) ? '' : formatDate(d, 'MM/dd/yyyy');
                                                                                },
                                                                            },
                                                                        },

                                                                        yAxis: [
                                                                            { title: { text: 'Weight (lbs)' } },
                                                                            { title: { text: 'BMI' }, opposite: true },
                                                                        ],

                                                                        series: [
                                                                            {
                                                                                name: 'Weight',
                                                                                data: buildSeriesData(historyData, 'Weight'),
                                                                                color: '#9c27b0',
                                                                                yAxis: 0,
                                                                            },
                                                                            {
                                                                                name: 'BMI',
                                                                                data: buildSeriesData(historyData, 'BMI'),
                                                                                color: '#ff9800',
                                                                                yAxis: 1,
                                                                            },
                                                                        ],

                                                                        tooltip: {
                                                                            shared: true,
                                                                            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                                                                                const d = new Date(this.x as number);

                                                                                const formattedDate = isNaN(d.getTime())
                                                                                    ? 'N/A'
                                                                                    : formatDate(d, 'MM/dd/yyyy hh:mm a');

                                                                                let tooltip = `<b>${formattedDate}</b><br/>`;

                                                                                this.points?.forEach((point) => {
                                                                                    const unit = point.series.name === 'Weight' ? ' lbs' : '';

                                                                                    tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y}${unit}</b><br/>`;
                                                                                });

                                                                                return tooltip;
                                                                            },
                                                                        },

                                                                        responsive: {
                                                                            rules: [
                                                                                {
                                                                                    condition: { maxWidth: 500 },
                                                                                    chartOptions: { legend: { enabled: false } },
                                                                                },
                                                                            ],
                                                                        },
                                                                    }}
                                                                    sx={{ height: { xs: 220, sm: 280, md: 320 } }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            </Collapse>
                                        </Grid>

                                        <Grid size={12}>
                                            <Typography variant="h6" gutterBottom>
                                                Vital Signs
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium" color="error">
                                                            {AssessmentQuestions.Vitals_HeartRate} <span>*</span> {values.IsBPPulseAi && isAIIcon(values.BpPulse)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="BpPulse"
                                                            name="BpPulse"
                                                            value={values.BpPulse}
                                                            error={values.BpPulse === '' && !formIsValid}
                                                            onBlur={handleBlur}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                setFieldValue('IsBPPulseAi', false);
                                                                setIsChanged(true);
                                                            }}
                                                            onKeyPress={preventTextInput}
                                                            inputProps={{ min: 0 }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium" color="error">
                                                            {AssessmentQuestions.Vitals_BloodPressure} <span>*</span> {values.IsBPAi && isAIIcon(values.BpSystolic || values.BpDiastolic)}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                label="Systolic"
                                                                name="BpSystolic"
                                                                value={values.BpSystolic}
                                                                error={values.BpSystolic === '' && !formIsValid}
                                                                onBlur={handleBlur}
                                                                onChange={(event) => {
                                                                    handleChange(event);
                                                                    setFieldValue('IsBPAi', false);
                                                                    setIsChanged(true);
                                                                }}
                                                                onKeyPress={preventTextInput}
                                                                inputProps={{ min: 0 }}
                                                                size={isSmallScreen ? 'medium' : 'small'}
                                                            />
                                                            <Typography variant="h6">/</Typography>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                label="Diastolic"
                                                                name="BpDiastolic"
                                                                value={values.BpDiastolic}
                                                                error={values.BpDiastolic === '' && !formIsValid}
                                                                onBlur={handleBlur}
                                                                onChange={(event) => {
                                                                    handleChange(event);
                                                                    setFieldValue('IsBPAi', false);
                                                                    setIsChanged(true);
                                                                }}
                                                                onKeyPress={preventTextInput}
                                                                inputProps={{ min: 0 }}
                                                                size={isSmallScreen ? 'medium' : 'small'}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_Temperature} {values.IsTemperatureAi && isAIIcon(values.Temperature)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="decimal"
                                                            label="Temperature (°F)"
                                                            name="Temperature"
                                                            value={values.Temperature}
                                                            inputProps={{ min: 0 }}
                                                            onKeyPress={preventTextInput}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                setFieldValue('IsTemperatureAi', false);
                                                                setIsChanged(true);
                                                            }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_PulseOximeter} {values.IsPulseOxSpO2LastAi && isAIIcon(values.PulseOxSpO2Last)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            label="SpO2 (%)"
                                                            name="PulseOxSpO2Last"
                                                            value={values.PulseOxSpO2Last}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                setFieldValue('IsPulseOxSpO2LastAi', false);
                                                                setIsChanged(true);
                                                                // Reset room air and O2 flow when SpO2 is cleared
                                                                if (!e.target.value) {
                                                                    setFieldValue('IsRoomAir', false);
                                                                    setFieldValue('O2Flow', '');
                                                                }
                                                            }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                        {values.PulseOxSpO2Last && (
                                                            <Box sx={{ mt: 1 }}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={values.IsRoomAir}
                                                                            onChange={(e) => {
                                                                                setIsChanged(true);
                                                                                setFieldValue('IsRoomAir', e.target.checked);
                                                                                if (e.target.checked) {
                                                                                    setFieldValue('O2FlowRate', '');
                                                                                }
                                                                            }}
                                                                            size="small"
                                                                        />
                                                                    }
                                                                    label={AssessmentQuestions.Vitals_IsRoomAir}
                                                                    sx={{ fontSize: '0.875rem' }}
                                                                />
                                                                {!values.IsRoomAir && (
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        label={AssessmentQuestions.Vitals_O2FlowRate}
                                                                        name="O2FlowRate"
                                                                        value={values.O2FlowRate}
                                                                        onChange={(event) => {
                                                                            handleChange(event);
                                                                            setIsChanged(true);
                                                                        }}
                                                                        inputProps={{ min: 0 }}
                                                                        onKeyPress={preventTextInput}
                                                                        InputProps={{
                                                                            endAdornment: <InputAdornment position="end">L/min</InputAdornment>,
                                                                        }}
                                                                        size={isSmallScreen ? 'medium' : 'small'}
                                                                        sx={{ mt: 1 }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_Glucose}
                                                            {values.IsGlucoseAi && isAIIcon(values.GlucoseMgDl)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="decimal"
                                                            label="mg/dL"
                                                            name="GlucoseMgDl"
                                                            value={values.GlucoseMgDl}
                                                            inputProps={{ min: 0 }}
                                                            onKeyPress={preventTextInput}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                setFieldValue('IsGlucoseAi', false);
                                                                setIsChanged(true);
                                                            }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Stack spacing={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {AssessmentQuestions.Vitals_RespirationRate}
                                                            {values.IsRespirationRateAi && isAIIcon(values.RespirationRate)}
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="rate"
                                                            name="RespirationRate"
                                                            value={values.RespirationRate}
                                                            inputProps={{ min: 0 }}
                                                            onKeyPress={preventTextInput}
                                                            onChange={(event) => {
                                                                handleChange(event);
                                                                setFieldValue('IsRespirationRateAi', false);
                                                                setIsChanged(true);
                                                            }}
                                                            size={isSmallScreen ? 'medium' : 'small'}
                                                        />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid size={12}>
                                            <Button
                                                onClick={() => setShowVitalChart(!showVitalChart)}
                                                startIcon={<TimelineIcon />}
                                                endIcon={showVitalChart ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                sx={{ mb: 2 }}
                                                variant="outlined"
                                                size="small"
                                                disabled={historyData && historyData.length < 2}
                                            >
                                                {showVitalChart ? 'Hide' : 'Show'} Vital Signs History
                                            </Button>
                                            <Collapse in={showVitalChart}>
                                                <Paper elevation={1} sx={{ p: 2 }}>
                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12, md: 12 }} pb={2}>
                                                            <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
                                                                <ChartWrapper
                                                                    options={{
                                                                        title: { text: 'Blood Pressure' },
                                                                        time: {
                                                                            useUTC: false,
                                                                        },
                                                                        chart: { type: 'line' },
                                                                        xAxis: {
                                                                            type: 'datetime',
                                                                            title: { text: 'Date' },
                                                                            labels: {
                                                                                step: 1,
                                                                                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                                                                                    return formatDate(new Date(this.value as number), 'MM/dd/yyyy');
                                                                                },
                                                                            },
                                                                        },
                                                                        yAxis: {
                                                                            title: { text: 'mmHg' },
                                                                        },
                                                                        series: [
                                                                            {
                                                                                name: 'Systolic',
                                                                                data: buildSeriesData(historyData, 'BpSystolic'),
                                                                                color: '#e53935',
                                                                            },
                                                                            {
                                                                                name: 'Diastolic',
                                                                                data: buildSeriesData(historyData, 'BpDiastolic'),
                                                                                color: '#1e88e5',
                                                                            },
                                                                        ],
                                                                        tooltip: {
                                                                            shared: true,
                                                                            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                                                                                const d = new Date(this.x as number);
                                                                                const formattedDate = !isValid(d) ? 'N/A' : formatDate(d, 'MM/dd/yyyy hh:mm a');

                                                                                let tooltip = `<b>${formattedDate}</b><br/>`;

                                                                                this.points?.forEach((point) => {
                                                                                    tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y} mmHg</b><br/>`;
                                                                                });

                                                                                return tooltip;
                                                                            },
                                                                        },
                                                                        responsive: {
                                                                            rules: [
                                                                                {
                                                                                    condition: { maxWidth: 500 },
                                                                                    chartOptions: {
                                                                                        legend: { enabled: false },
                                                                                    },
                                                                                },
                                                                            ],
                                                                        },
                                                                    }}
                                                                    sx={{ height: { xs: 200, sm: 240, md: 280 } }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 12 }} pb={2}>
                                                            <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
                                                                <ChartWrapper
                                                                    options={{
                                                                        title: { text: 'Heart Rate' },

                                                                        chart: { type: 'line' },

                                                                        time: {
                                                                            useUTC: false,
                                                                        },

                                                                        xAxis: {
                                                                            type: 'datetime',
                                                                            title: { text: 'Date' },
                                                                            labels: {
                                                                                step: 1,
                                                                                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                                                                                    const d = new Date(this.value as number);

                                                                                    return !isValid(d) ? '' : formatDate(d, 'MM/dd/yyyy');
                                                                                },
                                                                            },
                                                                        },

                                                                        yAxis: {
                                                                            title: { text: 'BPM' },
                                                                        },

                                                                        series: [
                                                                            {
                                                                                name: 'Heart Rate',
                                                                                data: historyData
                                                                                    .map((item: any) => {
                                                                                        const d = toDate(item.RecordingDate);
                                                                                        const value = Number(item.BpPulse);

                                                                                        if (!d || isNaN(d.getTime()) || !value) return null;

                                                                                        return [d.getTime(), value];
                                                                                    })
                                                                                    .filter(Boolean)
                                                                                    .sort((a: any, b: any) => a[0] - b[0]),

                                                                                color: '#d32f2f',
                                                                            },
                                                                        ],

                                                                        tooltip: {
                                                                            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                                                                                const d = new Date(this.x as number);

                                                                                const formattedDate = !isValid(d) ? 'N/A' : formatDate(d, 'MM/dd/yyyy hh:mm a');

                                                                                return `<b>${formattedDate}</b><br/>
                                                                                    <span style="color:${this.color}">●</span> ${this.series.name}: <b>${this.y} BPM</b>`;
                                                                            },
                                                                        },

                                                                        responsive: {
                                                                            rules: [
                                                                                {
                                                                                    condition: { maxWidth: 500 },
                                                                                    chartOptions: { legend: { enabled: false } },
                                                                                },
                                                                            ],
                                                                        },
                                                                    }}
                                                                    sx={{ height: { xs: 200, sm: 240, md: 280 } }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 12 }} pb={2}>
                                                            <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
                                                                <ChartWrapper
                                                                    options={{
                                                                        title: { text: 'Temperature History' },

                                                                        chart: { type: 'line' },

                                                                        time: {
                                                                            useUTC: false,
                                                                        },

                                                                        xAxis: {
                                                                            type: 'datetime',
                                                                            title: { text: 'Date' },
                                                                            labels: {
                                                                                step: 1,
                                                                                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                                                                                    const d = new Date(this.value as number);

                                                                                    return !isValid(d) ? '' : formatDate(d, 'MM/dd/yyyy');
                                                                                },
                                                                            },
                                                                        },

                                                                        yAxis: [
                                                                            {
                                                                                title: { text: 'Temperature (°F)' },
                                                                            },
                                                                        ],

                                                                        series: [
                                                                            {
                                                                                name: 'Temperature',
                                                                                data: historyData
                                                                                    .map((item: any) => {
                                                                                        const d = toDate(item.RecordingDate);
                                                                                        const value = Number(item.Temperature);

                                                                                        if (!d || isNaN(d.getTime()) || !value) return null;

                                                                                        return [d.getTime(), value];
                                                                                    })
                                                                                    .filter(Boolean)
                                                                                    .sort((a: any, b: any) => a[0] - b[0]),

                                                                                color: '#ff2222',
                                                                                yAxis: 0,
                                                                            },
                                                                        ],

                                                                        tooltip: {
                                                                            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                                                                                const d = new Date(this.x as number);

                                                                                const formattedDate = !isValid(d) ? 'N/A' : formatDate(d, 'MM/dd/yyyy hh:mm a');

                                                                                return `<b>${formattedDate}</b><br/>
                        <span style="color:${this.color}">●</span> ${this.series.name}: <b>${this.y}°F</b>`;
                                                                            },
                                                                        },

                                                                        responsive: {
                                                                            rules: [
                                                                                {
                                                                                    condition: { maxWidth: 500 },
                                                                                    chartOptions: { legend: { enabled: false } },
                                                                                },
                                                                            ],
                                                                        },
                                                                    }}
                                                                    sx={{ height: { xs: 220, sm: 280, md: 320 } }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 12 }} pb={2}>
                                                            <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
                                                                <ChartWrapper
                                                                    options={{
                                                                        title: { text: 'Respiration & SpO2' },

                                                                        chart: { type: 'line' },

                                                                        time: {
                                                                            useUTC: false,
                                                                        },

                                                                        xAxis: {
                                                                            type: 'datetime',
                                                                            title: { text: 'Date' },
                                                                            labels: {
                                                                                step: 1,
                                                                                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                                                                                    const d = new Date(this.value as number);

                                                                                    return !isValid(d) ? '' : formatDate(d, 'MM/dd/yyyy');
                                                                                },
                                                                            },
                                                                        },

                                                                        yAxis: [
                                                                            { title: { text: 'Respiration (rate)' } },
                                                                            { title: { text: 'SpO2 (%)' }, opposite: true },
                                                                        ],

                                                                        series: [
                                                                            {
                                                                                name: 'Respiration Rate',
                                                                                data: historyData
                                                                                    .map((item: any) => {
                                                                                        const d = toDate(item.RecordingDate);
                                                                                        const value = Number(item.RespirationRate);

                                                                                        if (!d || isNaN(d.getTime()) || !value) return null;

                                                                                        return [d.getTime(), value];
                                                                                    })
                                                                                    .filter(Boolean)
                                                                                    .sort((a: any, b: any) => a[0] - b[0]),

                                                                                color: '#6a1b9a',
                                                                                yAxis: 0,
                                                                            },
                                                                            {
                                                                                name: 'SpO2',
                                                                                data: historyData
                                                                                    .map((item: any) => {
                                                                                        const d = toDate(item.RecordingDate);
                                                                                        const value = Number(item.PulseOxSpO2Last);

                                                                                        if (!d || isNaN(d.getTime()) || !value) return null;

                                                                                        return [d.getTime(), value];
                                                                                    })
                                                                                    .filter(Boolean)
                                                                                    .sort((a: any, b: any) => a[0] - b[0]),

                                                                                color: '#00796b',
                                                                                yAxis: 1,
                                                                            },
                                                                        ],

                                                                        tooltip: {
                                                                            shared: true,
                                                                            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                                                                                const d = new Date(this.x as number);

                                                                                const formattedDate = !isValid(d) ? 'N/A' : formatDate(d, 'MM/dd/yyyy hh:mm a');

                                                                                let tooltip = `<b>${formattedDate}</b><br/>`;

                                                                                this.points?.forEach((point) => {
                                                                                    const unit = point.series.name === 'SpO2' ? '%' : ' rate';

                                                                                    tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y}${unit}</b><br/>`;
                                                                                });

                                                                                return tooltip;
                                                                            },
                                                                        },

                                                                        responsive: {
                                                                            rules: [
                                                                                {
                                                                                    condition: { maxWidth: 500 },
                                                                                    chartOptions: { legend: { enabled: false } },
                                                                                },
                                                                            ],
                                                                        },
                                                                    }}
                                                                    sx={{ height: { xs: 200, sm: 240, md: 280 } }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 12 }} pb={2}>
                                                            <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, width: '100%' }}>
                                                                <ChartWrapper
                                                                    options={{
                                                                        title: { text: 'Blood Glucose' },

                                                                        chart: { type: 'line' },

                                                                        time: {
                                                                            useUTC: false,
                                                                        },

                                                                        xAxis: {
                                                                            type: 'datetime',
                                                                            title: { text: 'Date' },
                                                                            tickInterval: 30 * 24 * 3600 * 1000, // fallback (approx month)

                                                                            tickPositioner: function (this: any) {
                                                                                const positions = [];

                                                                                const min = this.dataMin;
                                                                                const max = this.dataMax;

                                                                                if (
                                                                                    min == null ||
                                                                                    max == null ||
                                                                                    typeof min !== 'number' ||
                                                                                    typeof max !== 'number'
                                                                                ) {
                                                                                    return positions;
                                                                                }

                                                                                const start = new Date(min);
                                                                                start.setDate(1);
                                                                                start.setHours(0, 0, 0, 0);

                                                                                const end = new Date(max);

                                                                                let current = new Date(start);

                                                                                while (current <= end) {
                                                                                    positions.push(current.getTime());

                                                                                    // move to next month
                                                                                    current.setMonth(current.getMonth() + 1);
                                                                                }

                                                                                return positions;
                                                                            },

                                                                            labels: {
                                                                                step: 1,
                                                                                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                                                                                    const d = new Date(this.value as number);

                                                                                    return !isValid(d) ? '' : formatDate(d, 'MM/dd/yyyy');
                                                                                },
                                                                            },
                                                                        },

                                                                        yAxis: {
                                                                            title: { text: 'mg/dL' },
                                                                        },

                                                                        series: [
                                                                            {
                                                                                name: 'Glucose (mg/dL)',
                                                                                data: historyData
                                                                                    .map((item: any) => {
                                                                                        const d = toDate(item.RecordingDate);
                                                                                        const value = Number(item.GlucoseMgDl);

                                                                                        if (!d || isNaN(d.getTime()) || !value) return null;

                                                                                        return [d.getTime(), value];
                                                                                    })
                                                                                    .filter(Boolean)
                                                                                    .sort((a: any, b: any) => a[0] - b[0]),
                                                                                color: '#ff9800',
                                                                                yAxis: 0,
                                                                            },
                                                                        ],

                                                                        tooltip: {
                                                                            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                                                                                const d = new Date(this.x as number);

                                                                                const formattedDate = !isValid(d) ? 'N/A' : formatDate(d, 'MM/dd/yyyy hh:mm a');

                                                                                return `<b>${formattedDate}</b><br/>
                        <span style="color:${this.color}">●</span> ${this.series.name}: <b>${this.y} mg/dL</b>`;
                                                                            },
                                                                        },

                                                                        responsive: {
                                                                            rules: [
                                                                                {
                                                                                    condition: { maxWidth: 500 },
                                                                                    chartOptions: { legend: { enabled: false } },
                                                                                },
                                                                            ],
                                                                        },
                                                                    }}
                                                                    sx={{ height: { xs: 200, sm: 240, md: 280 } }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            </Collapse>
                                        </Grid>

                                        <Grid size={12}>
                                            <Divider sx={{ my: 2 }} />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        color="primary"
                                                        checked={values.RefusedForAssessment}
                                                        onChange={(e) => {
                                                            setFieldValue('RefusedForAssessment', e.currentTarget.checked);
                                                            setFieldValue('IsRefusedForAssessmentAi', false);
                                                            setIsChanged(true);
                                                        }}
                                                    />
                                                }
                                                label="Member refuses or is unable to conduct this assessment"
                                            />
                                            <Typography variant="body2" fontWeight="medium">
                                                {values.IsRefusedForAssessmentAi && isAIIcon(values.RefusedForAssessment)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Health Evaluation
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {AssessmentQuestions.Vitals_EvaluateYourHealth} {values.IsHealthEvaluationAi && isAIIcon(values.HealthEvaluation)}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <ImageList cols={isSmallScreen ? 3 : 5} sx={{ width: '100%' }}>
                                            {emojisList.map((item) => (
                                                <ImageListItem
                                                    key={item.emoji}
                                                    onClick={() => {
                                                        setFieldValue('HealthEvaluation', item.title);
                                                        setFieldValue('IsHealthEvaluationAi', false);
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        border: values.HealthEvaluation === item.title ? '3px solid' : '1px solid transparent',
                                                        borderColor: values.HealthEvaluation === item.title ? 'primary.main' : 'transparent',
                                                        borderRadius: 2,
                                                        p: 1,
                                                        width: '100px',
                                                        height: '100px',
                                                    }}
                                                >
                                                    <img src={item.emoji} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                    <Typography variant="caption" align="center" display="block">
                                                        {item.title}
                                                    </Typography>
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                        <RadioGroup
                                            row
                                            name="HealthEvaluation"
                                            value={values.HealthEvaluation}
                                            onChange={(event) => {
                                                handleChange(event);
                                                setFieldValue('IsHealthEvaluationAi', false);
                                                setIsChanged(true);
                                            }}
                                            sx={{ mt: 2 }}
                                        >
                                            <FormControlLabel value="Refuses to Answer" control={<Radio />} label="Refuses to Answer" />
                                            <FormControlLabel value="Didn't Ask" control={<Radio />} label="Didn't Ask" />
                                        </RadioGroup>
                                    </Box>

                                    <Box sx={{ mt: 3 }}>
                                        <Button
                                            onClick={() => setShowHealthChart(!showHealthChart)}
                                            startIcon={<TimelineIcon />}
                                            endIcon={showHealthChart ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            sx={{ mb: 2 }}
                                            variant="outlined"
                                            size="small"
                                            disabled={historyData && historyData.length < 2}
                                        >
                                            {showHealthChart ? 'Hide' : 'Show'} Health Evaluation History
                                        </Button>
                                        <Collapse in={showHealthChart}>
                                            <Paper elevation={1} sx={{ p: 2 }}>
                                                <Box sx={{ height: { xs: 220, sm: 280, md: 320 }, width: '100%' }}>
                                                    <div style={{ height: '100%', display: 'flex' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <ChartWrapper
                                                                options={{
                                                                    title: { text: 'Health Evaluation Over Time' },
                                                                    xAxis: {
                                                                        type: 'datetime',
                                                                        title: { text: 'Date' },
                                                                        labels: {
                                                                            formatter: function (this: any) {
                                                                                return (
                                                                                    formatDate(this.value, 'MM/dd/yyyy') || formatDate(new Date(), 'MM/dd/yyyy')
                                                                                );
                                                                            },
                                                                        },
                                                                    },
                                                                    yAxis: {
                                                                        title: { text: 'Health Score' },
                                                                        min: 1,
                                                                        max: 5,
                                                                        tickInterval: 1,
                                                                        labels: {
                                                                            formatter: function (this: any) {
                                                                                const labels = ['', 'Very Bad', 'Bad', 'Ok', 'Good', 'Very Good'];
                                                                                return labels[this.value];
                                                                            },
                                                                        },
                                                                    },
                                                                    series: [
                                                                        {
                                                                            name: 'Health Evaluation',
                                                                            data: historyData
                                                                                .map((item) => {
                                                                                    let score = 0;
                                                                                    if (item.HealthEvaluation === 'Very Bad') score = 1;
                                                                                    else if (item.HealthEvaluation === 'Bad') score = 2;
                                                                                    else if (item.HealthEvaluation === 'Ok') score = 3;
                                                                                    else if (item.HealthEvaluation === 'Good') score = 4;
                                                                                    else if (item.HealthEvaluation === 'Very Good') score = 5;
                                                                                    return score > 0
                                                                                        ? [(toDate(item.RecordingDate) || new Date()).getTime(), score]
                                                                                        : null;
                                                                                })
                                                                                .filter((item) => item !== null),
                                                                            color: '#4caf50',
                                                                            marker: { symbol: 'circle', radius: 6 },
                                                                        },
                                                                    ],
                                                                    chart: { type: 'line' },
                                                                    tooltip: {
                                                                        formatter: function (this: any) {
                                                                            const labels = ['', 'Very Bad', 'Bad', 'Ok', 'Good', 'Very Good'];
                                                                            return `<b>${this.series.name}</b><br/>${
                                                                                formatDate(this.x, 'MM/dd/yyyy h:mm a') ||
                                                                                formatDate(new Date(), 'MM/dd/yyyy h:mm a')
                                                                            }<br/>${labels[this.y]}`;
                                                                        },
                                                                    },
                                                                    responsive: {
                                                                        rules: [{ condition: { maxWidth: 500 }, chartOptions: { legend: { enabled: false } } }],
                                                                    },
                                                                }}
                                                                sx={{ height: { xs: 220, sm: 280, md: 320 } }}
                                                            />
                                                        </div>
                                                    </div>
                                                </Box>
                                            </Paper>
                                        </Collapse>
                                    </Box>
                                </Paper>

                                <Grid size={12} className="content-footer" width={'100%'}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ mr: 3 }}
                                        disabled={isSaving}
                                        onClick={() => dispatch(gotoPreviousMenu())}
                                        startIcon={<ArrowBackIosIcon />}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSaving}
                                        endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
                                    >
                                        Next
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Paper>

                    {/* Alert Dialogs remain the same */}
                    {Boolean(engagementDraft.VitalsignAssessmentId) || isChanged ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Vital Signs"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(values, true)}
                            onSkip={() => handleOnConfirmToNext(values)}
                            showBottomCenter={true}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message="I confirm and acknowledge that Vital Signs is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Vital Signs"
                            cancelText="No"
                            okText="Yes, Proceed"
                            onConfirm={() => handleOnConfirmToNext(values)}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            showBottomCenter={true}
                            message="Are you sure to proceed without completing Vital Signs?"
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default VitalSigns;
